import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform, AnimatePresence } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { IMG } from "../../lib/content";
import { scrollToId } from "./Reveal";

const LINES = [
  { t: "Jen", c: "" },
  { t: "jedno", c: "text-outline" },
  { t: "slovo.", c: "text-[var(--cyan)]" },
];
const WORDS = ["Wow.", "Lahoda.", "Nadčasové.", "Dokonalé."];
const ease = [0.16, 1, 0.3, 1];

const RotatingWord = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % WORDS.length), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="relative inline-flex overflow-hidden align-bottom h-[1.25em] min-w-[7ch]">
      <AnimatePresence initial={false}>
        <motion.span key={WORDS[i]} className="absolute left-0 text-[var(--amber)]"
          initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "-100%" }} transition={{ duration: 0.6, ease }}>
          {WORDS[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

const Portal = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} style={{ perspective: 1200 }} className="relative w-full aspect-square max-w-[520px] mx-auto">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative w-full h-full">
        <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full spin-slow" style={{ transform: "translateZ(60px)" }}>
          <circle cx="200" cy="200" r="196" fill="none" stroke="rgba(46,91,37,.4)" strokeDasharray="2 8" />
          <circle cx="200" cy="4" r="5" fill="#C08A2E" />
        </svg>
        <motion.div
          initial={{ clipPath: "circle(0% at 50% 50%)" }}
          animate={{ clipPath: "circle(42% at 50% 50%)" }}
          transition={{ duration: 1.6, delay: 0.6, ease }}
          className="absolute inset-0 overflow-hidden"
        >
          <img src={IMG.hero} alt="Gurmánský talíř" className="w-full h-full object-cover scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#14181f]/25 via-transparent to-transparent" />
          <div className="absolute inset-x-0 h-1/3 bg-gradient-to-b from-transparent via-[rgba(255,255,255,.18)] to-transparent scanline" />
        </motion.div>
        <div className="absolute left-[2%] bottom-[14%] font-mono text-[11px] uppercase tracking-[0.2em] bg-white/90 shadow-lg border border-[var(--line)] px-3 py-2" style={{ transform: "translateZ(90px)" }}>
          <span className="text-[var(--cyan)]">●</span> Čerstvě připraveno
        </div>
        <div className="absolute right-[2%] top-[16%] font-mono text-[11px] uppercase tracking-[0.2em] bg-white/90 shadow-lg border border-[var(--line)] px-3 py-2" style={{ transform: "translateZ(120px)" }}>
          Menu <span className="text-[var(--amber)]">na míru</span>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yPortal = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] pt-[100px] pb-16 overflow-hidden grid-bg">
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[var(--glow)] opacity-40 blur-[120px]" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--muted)] mb-6 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[var(--cyan)] blink" /> Catering pro všechny příležitosti
          </motion.p>
          <h1 data-testid="hero-title" className="font-display font-extrabold uppercase tracking-tight leading-[0.92] text-[15vw] sm:text-7xl lg:text-[7.4rem]">
            {LINES.map((l, i) => (
              <span key={l.t} className="block overflow-hidden pb-[0.06em]">
                <motion.span className={`block ${l.c}`} initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease }}>
                  {l.t}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}>
            <p className="mt-8 font-display text-lg md:text-2xl font-light">
              A to slovo bude: <RotatingWord />
            </p>
            <p className="mt-5 max-w-xl text-base md:text-lg text-[var(--muted)] leading-relaxed">
              Dodáváme jídlo na svatby, oslavy, zahradní párty i firemní akce. Moderní gastronomie s precizností, která předbíhá dobu.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button data-testid="hero-cta-inquiry" onClick={() => scrollToId("poptavka")} className="group rounded-full bg-[var(--cyan)] text-white pl-7 pr-2 py-2 font-semibold flex items-center gap-4 hover:bg-[var(--ink)] transition-colors">
                Nezávazná poptávka
                <span className="w-10 h-10 rounded-full bg-white text-[var(--cyan)] grid place-items-center transition-transform duration-500 group-hover:-rotate-45"><ArrowDownRight size={18} /></span>
              </button>
              <button data-testid="hero-cta-menu" onClick={() => scrollToId("nabidka")} className="rounded-full border border-[var(--line)] px-7 py-4 font-semibold hover:border-[var(--cyan)] hover:text-[var(--cyan)] transition-colors">
                Prohlédnout nabídku
              </button>
            </div>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yPortal }} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.4, ease }} className="lg:col-span-5">
          <Portal />
        </motion.div>
      </div>
    </section>
  );
};
